import Head from "next/head";
import { Flex, Box, Text } from "rebass/styled-components";
import useTranslation from "next-translate/useTranslation";
import Trans from "next-translate/Trans";

import Heading from "./Heading";
import Icon from "./Icon";
import Rating from "./Rating";
import theme from "../../lib/theme";
import { SearchQuery } from "../../generated/graphql";

interface Props {
  city?: SearchQuery["search"]["city"];
  serviceType?: SearchQuery["search"]["serviceType"];
  title?: string;
  introHtml?: string;
  contentHtml?: string;
}

const CityServiceDescription = ({
  city,
  serviceType,
  title,
  introHtml,
  contentHtml
}: Props) => {
  const { t } = useTranslation("common");

  let heading = "";
  let description = "";
  let sumCaregiverReviewsCount = 0;
  let activeSitters = 0;
  let avgPrice = 0;
  let minPrice = 0;
  let serviceIconName = "services/boarding";
  let servicePeriod = t("night");

  if (city && !serviceType) {
    heading = t("serviceTypeDefaultIn") + " " + city.name;
    description = city?.stats
      ? `common:dogSittersInCityDescription`
      : `common:dogSittersInCityDescriptionDefault`;
    sumCaregiverReviewsCount =
      city?.stats?.sitterReviews || city?.sumCaregiverReviewsCount || 0;
    activeSitters = city?.stats?.activeSitters || 0;
    avgPrice = city?.stats?.boardingAvgPricePerNight || 0;
    minPrice = city?.stats?.boardingMinSitterRate || 0;
  }

  if (!city && serviceType) {
    heading = t(`serviceType${serviceType.id}Service`);
    description = `common:serviceType${serviceType.id}Description`;
  }

  if (city && serviceType) {
    heading = t(`serviceType${serviceType.id}In`, { city: city.name });
    description = city?.stats
      ? `common:serviceType${serviceType.id}InCityDescription`
      : `common:serviceType${serviceType.id}InCityDescriptionDefault`;
    switch (serviceType.id) {
      case "1":
        sumCaregiverReviewsCount = city?.stats?.boardingSitterReviews || 0;
        activeSitters = city?.stats?.boardingActiveSitters || 0;
        avgPrice = city?.stats?.boardingAvgPricePerNight || 0;
        minPrice = city?.stats?.boardingMinSitterRate || 0;
        serviceIconName = "services/boarding";
        servicePeriod = t("night");
        break;
      case "2":
        sumCaregiverReviewsCount = city?.stats?.walkingSitterReviews || 0;
        activeSitters = city?.stats?.walkingActiveSitters || 0;
        avgPrice = city?.stats?.walkingAvgPricePerWalk || 0;
        minPrice = city?.stats?.walkingMinSitterRate || 0;
        serviceIconName = "services/dog-walking";
        servicePeriod = t("walk");
        break;
      case "7":
        sumCaregiverReviewsCount = city?.stats?.daycareSitterReviews || 0;
        activeSitters = city?.stats?.daycareActiveSitters || 0;
        avgPrice = city?.stats?.daycareAvgPricePerDay || 0;
        minPrice = city?.stats?.daycareMinSitterRate || 0;
        serviceIconName = "services/day-care";
        servicePeriod = t("day");
        break;
      default:
        break;
    }
  }

  if (!!title) {
    heading = title;
  }

  const stats = city
    ? [
        activeSitters && {
          iconName: "vetted",
          title: serviceType?.id
            ? t(`cityStatsItem1TitleServiceType${serviceType.id}`, {
                activeSitters,
                cityName: city.name
              })
            : t("cityStatsItem1Title", {
                activeSitters,
                cityName: city.name
              }),
          description: t("cityStatsItem1Desc")
        },
        avgPrice && {
          iconName: serviceIconName || "services/boarding",
          title: serviceType?.id
            ? t(`cityStatsItem2TitleServiceType${serviceType.id}`, {
                avgPrice,
                servicePeriod
              })
            : t("cityStatsItem2Title", { avgPrice, servicePeriod }),
          description: t("cityStatsItem2Desc")
        },
        sumCaregiverReviewsCount && {
          iconName: "star-off",
          title: serviceType?.id
            ? t(`cityStatsItem3TitleServiceType${serviceType.id}`, {
                sumCaregiverReviewsCount,
                avgCaregiverReviewScore: city.avgCaregiverReviewsScore,
                cityName: city.name
              })
            : t("cityStatsItem3Title", { sumCaregiverReviewsCount }),
          description: t("cityStatsItem3Desc", {
            avgCaregiverReviewsScore: city.avgCaregiverReviewsScore
          })
        },
        minPrice && {
          iconName: "gudog",
          title: serviceType?.id
            ? t(`cityStatsItem4TitleServiceType${serviceType.id}`, {
                minPrice,
                servicePeriod
              })
            : t("cityStatsItem4Title", { minPrice, servicePeriod }),
          description: t("cityStatsItem4Desc")
        }
      ].filter(el => !!el)
    : [];

  const defaultContentHtml = t(description, {
    city: city?.name,
    ...(city?.stats ?? {}),
    boardingCompletedNights:
      city?.stats &&
      Math.ceil(
        city?.stats?.boardingCompletedBookings * city?.stats?.boardingAvgNights
      ) + 1,
    walkingCompletedWalks:
      city?.stats &&
      Math.ceil(
        city?.stats?.walkingCompletedBookings * city?.stats?.walkingAvgWalks
      ),
    daycareCompletedDays:
      city?.stats &&
      Math.ceil(
        city?.stats?.daycareCompletedBookings * city?.stats?.daycareAvgDays
      )
  });

  let faqs = !contentHtml ? convertFAQsStringToArray(defaultContentHtml) : [];

  return (
    <>
      {faqs && faqs.length > 0 && (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: `{
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": ${JSON.stringify(
                faqs.map(faq => ({
                  "@type": "Question",
                  name: faq.question,
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: faq.answer
                  }
                }))
              )}
            }`
            }}
          />
        </Head>
      )}

      <Box px={[0, 0, 0]}>
        <Box
          sx={{
            backgroundColor: "samoyedWhite",
            borderRadius: 2,
            boxShadow: "inputFocus"
          }}
          px={[3, 3, 8]}
          py={[3, 3, 5]}
        >
          <Box>
            <Heading as="h2">{heading}</Heading>

            {city && sumCaregiverReviewsCount > 0 && (
              <Flex alignItems="center">
                <Rating score={city.avgCaregiverReviewsScore}></Rating>

                <Text ml={1} mt="2px">
                  <Text
                    as="span"
                    sx={{
                      color: "labradorBlack",
                      fontSize: 2,
                      lineHeight: 1
                    }}
                  >
                    {Math.round(city.avgCaregiverReviewsScore)}
                  </Text>
                  <Text
                    as="span"
                    sx={{
                      color: "darkGray",
                      fontSize: 2,
                      lineHeight: 1
                    }}
                  >
                    {" "}
                    ({sumCaregiverReviewsCount}{" "}
                    <Trans i18nKey="common:reviews" />)
                  </Text>
                </Text>
              </Flex>
            )}

            {stats && stats.length > 0 && (
              <Flex
                mt={3}
                mb={[2, 3]}
                pb={[4, 4]}
                flexDirection={["column", "row"]}
                sx={{
                  gap: ["16px", "64px"],
                  borderBottom: "1px solid lightGrey"
                }}
              >
                {stats.map(({ iconName, title, description }: any) => (
                  <Flex
                    width={["100%", "50%"]}
                    flexDirection={["row", "column"]}
                  >
                    <Box flexShrink={0} mt={[1, 0]} mr={[2, 0]}>
                      <Icon name={iconName} fontSize={6} color="inuOrange" />
                    </Box>
                    <Box mt={1}>
                      <Heading as="h3" fontSize={5} fontWeight="book">
                        {title}
                      </Heading>
                    </Box>
                  </Flex>
                ))}
              </Flex>
            )}

            <Box width={[1, 1, 1]}>
              {!!introHtml && (
                <Box
                  dangerouslySetInnerHTML={{
                    __html: introHtml.trim().replace(/(\r\n|\n|\r)/gm, "")
                  }}
                  className="seoContent"
                  sx={seoContentHtmlStyle}
                ></Box>
              )}

              {!contentHtml ? (
                // fallback text
                <Text
                  sx={{
                    pt: 1,
                    whiteSpace: "pre-wrap"
                  }}
                >
                  <Box
                    dangerouslySetInnerHTML={{
                      __html: defaultContentHtml
                        .trim()
                        .replace(/(\r\n|\n|\r)/gm, "")
                    }}
                    className="seoContent"
                    sx={seoContentHtmlStyle}
                  />
                </Text>
              ) : (
                <Box
                  dangerouslySetInnerHTML={{
                    __html: contentHtml.trim().replace(/(\r\n|\n|\r)/gm, "")
                  }}
                  className="seoContent"
                  sx={seoContentHtmlStyle}
                ></Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

const seoContentHtmlStyle = {
  whiteSpace: "pre-wrap",
  pt: 1,
  "*": {
    margin: 0,
    padding: 0
  },
  ul: {
    marginLeft: 3
  },
  ".faq": {
    marginBottom: 4
  },
  "p, li": {
    textAlign: "left",
    marginBottom: 2,
    color: "#9A9A9A",
    strong: {
      color: "#181818",
      fontWeight: 300
    },
    lineHeight: "24px"
  },
  li: {
    marginBottom: 1
  },
  "h1, h2, h3, h4": {
    textAlign: "left",
    marginBottom: 2
  },
  ...theme.text,
  ".section": {
    marginBottom: 2
  },
  a: {
    color: "#1F8C8D",
    fontWeight: 300
  },
  iframe: {
    width: "100%"
  },
  ".row": {
    display: "flex",
    flexDirection: ["column", "column", "row"]
  }
};

const convertFAQsStringToArray = (str: string) => {
  const faqBlocksRegex = /<div class='faq'>(.+?)<\/div>/gs;
  const matches = str.matchAll(faqBlocksRegex);

  const faqs = Array.from(matches).map(match => {
    const faqContent = match[1];

    const questionMatch = /<h3>(.*?)<\/h3>/.exec(faqContent);

    const question = questionMatch ? questionMatch[1] : "";
    const answer = questionMatch
      ? faqContent
          .substring(questionMatch[0].length)
          .replace(/<[^>]*>/g, " ")
          .trim()
          .replace(/\s\s+/g, " ")
      : "";

    return { question, answer };
  });

  return faqs;
};

export default CityServiceDescription;
